import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, ListTree, ChevronUp, ChevronDown, Pencil, Trash2, ChevronRight } from "lucide-react";
import { EmptyState } from "@/components/common/EmptyState";
import { EntryModal } from "@/components/common/EntryModal";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ContentItemsPanel } from "./ContentItemsPanel";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useResource } from "@/hooks/useResource";
import { useUser } from "@/context/UserContext";
import { OWNERS, CHAPTER_STATUS, PRODUCTION_STATUS, PRODUCTION_AREAS, productBadge } from "@/data/productOptions";

const AREA_DOT = (v) =>
  v === "Complete" ? "#16A34A" : v === "Blocked" ? "#FB6F5A" : v === "In progress" ? "#F97316" : v === "Ready for review" ? "#1D4ED8" : "#CBD5EA";

const chapterFields = () => [
  { name: "name", label: "Chapter name", type: "text", placeholder: "e.g. The Lost Compass", required: true },
  { name: "owner", label: "Owner", type: "select", placeholder: "Owner", options: OWNERS },
  { name: "status", label: "Overall status", type: "select", placeholder: "Status", options: CHAPTER_STATUS, required: true },
  { name: "completion", label: "Completion %", type: "text", placeholder: "0 - 100" },
  { name: "due_date", label: "Due date", type: "date" },
  { name: "description", label: "Short description", type: "textarea", placeholder: "What happens in this chapter?" },
  ...PRODUCTION_AREAS.map((a) => ({ name: a.key, label: `${a.label} status`, type: "select", placeholder: a.label, options: PRODUCTION_STATUS })),
  { name: "current_blocker", label: "Current blocker", type: "textarea" },
  { name: "next_action", label: "Next action", type: "textarea" },
  { name: "notes", label: "Notes", type: "textarea" },
];

export const ChaptersPanel = ({ moduleId, onChange }) => {
  const { canWrite } = useUser();
  const { items: all, refetch } = useResource("chapters");
  const { items: allItems } = useResource("content-items");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [statusF, setStatusF] = useState("all");
  const [sort, setSort] = useState("number");

  const chapters = useMemo(() => {
    let rows = all.filter((c) => c.module_id === moduleId);
    if (statusF !== "all") rows = rows.filter((c) => c.status === statusF);
    const sorters = {
      number: (a, b) => a.chapter_number - b.chapter_number,
      progress: (a, b) => (b.completion || 0) - (a.completion || 0),
      due: (a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"),
    };
    return [...rows].sort(sorters[sort]);
  }, [all, moduleId, statusF, sort]);

  const openAdd = () => { setEditing({ module_id: moduleId, owner: "Steven", status: "Not started", completion: 0 }); setModalOpen(true); };
  const openEdit = (c) => { setEditing(c); setModalOpen(true); };

  const handleSave = async (v) => {
    if (v.id) { await api.update("chapters", v.id, v); toast.success("Chapter updated"); }
    else { await api.create("chapters", { ...v, module_id: moduleId }); toast.success("Chapter added"); }
    await refetch(); onChange?.();
  };

  const move = async (index, dir) => {
    const ordered = all.filter((c) => c.module_id === moduleId).sort((a, b) => a.chapter_number - b.chapter_number);
    const arr = [...ordered];
    const target = index + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    await api.reorder("chapters", arr.map((c) => c.id));
    await refetch();
  };

  const doDelete = async (reason) => {
    await api.remove("chapters", confirmId, reason);
    toast.success("Moved to Recycle Bin");
    await refetch(); onChange?.();
    setConfirmId(null);
  };

  return (
    <div data-testid="chapters-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-[#F97316] text-white shadow-sm"><ListTree size={18} strokeWidth={2.2} /></span>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#F97316]">Journey Path</p>
            <h3 className="font-heading text-lg font-semibold text-[#14245F]">Chapters</h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusF} onValueChange={setStatusF}>
            <SelectTrigger data-testid="chapters-filter-status" className="h-9 w-auto min-w-[120px] rounded-full border-[#E4E9F3] bg-white"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Status: All</SelectItem>{CHAPTER_STATUS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="chapters-sort" className="h-9 w-auto min-w-[120px] rounded-full border-[#E4E9F3] bg-white"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="number">Sort: Order</SelectItem><SelectItem value="progress">Sort: Progress</SelectItem><SelectItem value="due">Sort: Due date</SelectItem></SelectContent>
          </Select>
          {canWrite && <Button size="sm" data-testid="add-chapter-btn" onClick={openAdd} className="rounded-full bg-[#F97316] text-white hover:bg-[#EA6A0C]"><Plus size={15} /> Add Chapter</Button>}
        </div>
      </div>

      {chapters.length === 0 ? (
        <EmptyState icon={ListTree} title="No chapters on this journey yet" description="Break this module down into chapters to track the expedition's progress." actionLabel="Add chapter" onAction={openAdd} />
      ) : (
        <ul className="relative space-y-3 before:absolute before:bottom-6 before:left-[26px] before:top-6 before:w-0.5 before:bg-[#EEF2FB]" data-testid="chapters-list">
          {chapters.map((c, i) => {
            const done = c.status === "Complete";
            const nodeColor = done ? "#16A34A" : c.status === "Blocked" ? "#FB6F5A" : c.status === "In progress" ? "#F97316" : "#CBD5EA";
            return (
            <li key={c.id} data-testid={`chapter-row-${c.id}`} className="relative rounded-2xl border border-[#E4E9F3] bg-white p-4 shadow-[0_2px_12px_rgba(20,36,95,0.05)]">
              <div className="flex items-center gap-3">
                {canWrite && (
                <div className="flex flex-col">
                  <button data-testid={`chapter-up-${c.id}`} disabled={i === 0} onClick={() => move(i, -1)} className="text-[#9DAAC6] disabled:opacity-30 hover:text-[#14245F]"><ChevronUp size={16} /></button>
                  <button data-testid={`chapter-down-${c.id}`} disabled={i === chapters.length - 1} onClick={() => move(i, 1)} className="text-[#9DAAC6] disabled:opacity-30 hover:text-[#14245F]"><ChevronDown size={16} /></button>
                </div>
                )}
                {/* journey node */}
                <span className="z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-numeric text-xs font-bold text-white ring-4 ring-white" style={{ backgroundColor: nodeColor }}>
                  {c.chapter_number}
                </span>
                <button data-testid={`toggle-chapter-${c.id}`} onClick={() => setExpanded(expanded === c.id ? null : c.id)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                  <ChevronRight size={16} className={`shrink-0 text-[#9DAAC6] transition-transform ${expanded === c.id ? "rotate-90" : ""}`} />
                  <span className="truncate font-semibold text-[#14245F]">{c.name}</span>
                </button>
                <StatusBadge status={productBadge(c.status)} label={c.status} />
                <div className="hidden w-28 md:block">
                  <div className="h-2 w-full overflow-hidden rounded-full bg-[#EEF2FB]">
                    <div className="h-full rounded-full bg-[#F97316]" style={{ width: `${Math.max(0, Math.min(100, Number(c.completion) || 0))}%` }} />
                  </div>
                </div>
                {canWrite && <Button size="icon" variant="ghost" data-testid={`edit-chapter-${c.id}`} onClick={() => openEdit(c)} className="h-8 w-8 rounded-full text-[#5A6B8C] hover:text-[#14245F]"><Pencil size={15} /></Button>}
                {canWrite && <Button size="icon" variant="ghost" data-testid={`delete-chapter-${c.id}`} onClick={() => setConfirmId(c.id)} className="h-8 w-8 rounded-full text-[#5A6B8C] hover:text-[#DC2626]"><Trash2 size={15} /></Button>}
              </div>

              <div className="mt-3 flex flex-wrap gap-1.5 pl-[52px]">
                {PRODUCTION_AREAS.map((a) => (
                  <span key={a.key} className="inline-flex items-center gap-1 rounded-full bg-[#F4F6FB] px-2 py-0.5 text-[10px] font-semibold text-[#5A6B8C] ring-1 ring-[#E4E9F3]">
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: AREA_DOT(c[a.key]) }} />
                    {a.label}
                  </span>
                ))}
              </div>

              {expanded === c.id && <ContentItemsPanel chapterId={c.id} onChange={onChange} />}
            </li>
            );
          })}
        </ul>
      )}

      <EntryModal open={modalOpen} onOpenChange={setModalOpen} title={editing?.id ? "Edit Chapter" : "Add Chapter"} submitLabel={editing?.id ? "Save Changes" : "Add Chapter"} fields={chapterFields()} initialValues={editing} onSubmit={handleSave} />
      <ConfirmDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)} title="Delete this chapter?" softDelete linkedCount={confirmId ? allItems.filter((it) => it.chapter_id === confirmId).length : 0} onConfirm={doDelete} />
    </div>
  );
};
